// The minimap (top right; N hides it) and the world map (M): how the districts, the Town Gate and the
// Meadowlands fit together, where you are, and where your party and friends are.
import { ZONES, zoneDef } from '../../../shared/zones.js';
import { INTERIORS } from '../../../shared/interiors.js';
import type { Scene } from '../render/scene.js';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
export interface Dot { x: number; y: number; kind: 'me' | 'party' | 'player' | 'npc' | 'mob' | 'boss'; dir?: number }
export interface Mark { x: number; y: number; kind: 'exit' | 'door' | 'shrine' | 'battle'; label: string; open?: boolean; r?: number }

/** Where a zone name (as the server reports it) is on the world map. */
export function zoneOfName(name: string): string | null {
  const z = ZONES.find(z => z.name === name); if (z) return z.id;
  if (INTERIORS.some(r => r.name === name && r.id !== 'home')) return 'town';
  if (/Cottage$/.test(name)) return 'cottages';
  return null;
}

const LAYOUT: Record<string, [number, number]> = { artisan: [1, 0], harbor: [0, 1], town: [1, 1], cottages: [2, 1], gate: [1, 2], meadow: [1, 3] };
const LINKS: [string, string][] = [['town', 'artisan'], ['town', 'harbor'], ['town', 'cottages'], ['town', 'gate'], ['gate', 'meadow']];
const BLURB: Record<string, string> = {
  town: 'The fountain, the bakery, the guild hall.', harbor: 'Boats, piers and the fish market.', artisan: 'The smithy, the carpenter and the weaver.',
  cottages: 'Every door here leads to your own home.', gate: 'The way out of town.', meadow: 'Open world · creatures Lv 1–3 · mounts soon',
};

export class Maps {
  private mini = $<HTMLCanvasElement>('minimap'); private mctx = this.mini.getContext('2d')!;
  private box = $('minimapBox');
  private world = $('worldmap');
  shown = true;
  onOpen: (open: boolean) => void = () => {};
  /** Filled in by the game: scenes by zone id (painted on first visit), and where people are. */
  scene: (id: string) => Scene | null = () => null;
  people: () => { party: Map<string, string[]>; friends: Map<string, string[]> } = () => ({ party: new Map(), friends: new Map() });

  constructor() {
    try { this.shown = localStorage.getItem('tt-minimap') !== 'off'; } catch { /* private mode */ }
    this.box.hidden = true;
    this.box.addEventListener('click', () => this.toggleWorld(true));
    $('worldClose').addEventListener('click', () => this.toggleWorld(false));
    this.world.addEventListener('click', e => { if (e.target === this.world) this.toggleWorld(false); });
    addEventListener('keydown', e => {
      if (/INPUT|TEXTAREA|SELECT/.test((document.activeElement as HTMLElement)?.tagName || '') || this.box.dataset.off === '1') return;
      if (e.code === 'KeyM' && !e.repeat) { e.preventDefault(); this.toggleWorld(); }
      else if (e.code === 'KeyN' && !e.repeat) { e.preventDefault(); this.shown = !this.shown; try { localStorage.setItem('tt-minimap', this.shown ? 'on' : 'off'); } catch { /* fine */ } this.box.classList.toggle('off', !this.shown); }
      else if (e.code === 'Escape' && !this.world.hidden) { e.preventDefault(); this.toggleWorld(false); }
    });
    this.box.classList.toggle('off', !this.shown);
  }

  /** In the world (or not): the minimap shows only while playing. */
  setPlaying(on: boolean): void { this.box.hidden = !on; this.box.dataset.off = on ? '' : '1'; if (!on) this.toggleWorld(false); }

  private zone = ''; private name = '';
  setZone(id: string, name: string, layer: number): void {
    this.zone = id; this.name = name;
    $('minimapName').textContent = `${name}${layer > 1 ? ` · ${layer}` : ''}`;
    if (!this.world.hidden) this.renderWorld();
  }

  /** Redraw the minimap: the zone's picture around you, marks, and dots. Cheap: call a few times a second. */
  draw(scene: Scene | null, px: number, py: number, dots: Dot[], marks: Mark[], now: number): void {
    if (!this.shown || this.box.hidden) return;
    const c = this.mctx, W = this.mini.width, H = this.mini.height, Z = 3;   // pixels per tile
    c.imageSmoothingEnabled = false;
    c.fillStyle = '#2f2a26'; c.fillRect(0, 0, W, H);
    if (!scene) { c.fillStyle = '#efe4c8'; c.font = '700 10px system-ui'; c.textAlign = 'center'; c.fillText('Indoors', W / 2, H / 2 + 3); return; }
    const tx = px / 16, ty = py / 16;
    let ox = W / 2 - tx * Z, oy = H / 2 - ty * Z;
    // keep the map edge in view rather than showing empty space, when the zone is bigger than the window
    if (scene.w * Z > W) ox = Math.min(0, Math.max(W - scene.w * Z, ox)); else ox = (W - scene.w * Z) / 2;
    if (scene.h * Z > H) oy = Math.min(0, Math.max(H - scene.h * Z, oy)); else oy = (H - scene.h * Z) / 2;
    c.drawImage(scene.thumb(), Math.round(ox), Math.round(oy), scene.w * Z, scene.h * Z);
    const P = (x: number, y: number): [number, number] => [Math.round(ox + x / 16 * Z), Math.round(oy + y / 16 * Z)];
    for (const m of marks) {
      const [x, y] = P(m.x, m.y);
      if (m.kind === 'battle') { c.strokeStyle = m.open ? '#8fe08a' : '#ec5448'; c.lineWidth = 1; c.beginPath(); c.arc(x, y, Math.max(3, (m.r ?? 40) / 16 * Z), 0, Math.PI * 2); c.stroke(); c.fillStyle = m.open ? '#8fe08a' : '#ec5448'; c.fillRect(x - 1, y - 1, 2, 2); }
      else if (m.kind === 'shrine') { c.fillStyle = '#2f2a26'; c.fillRect(x - 3, y - 3, 7, 7); c.fillStyle = '#a3def4'; c.fillRect(x - 2, y - 2, 5, 5); c.fillStyle = '#f2fbff'; c.fillRect(x - 1, y - 1, 2, 2); }
      else if (m.kind === 'exit') { const pulse = Math.floor(now / 400) % 2; c.fillStyle = '#2f2a26'; c.fillRect(x - 3, y - 3, 7, 7); c.fillStyle = pulse ? '#ffe9a8' : '#f9c776'; c.fillRect(x - 2, y - 2, 5, 5); }
      else { c.fillStyle = '#7a473f'; c.fillRect(x - 1, y - 1, 3, 3); }
    }
    const order = { npc: 0, player: 1, mob: 2, boss: 3, party: 4, me: 5 };
    for (const d of dots.sort((a, b) => order[a.kind] - order[b.kind])) {
      const [x, y] = P(d.x, d.y);
      if (x < -3 || y < -3 || x > W + 3 || y > H + 3) continue;
      if (d.kind === 'me') {
        c.fillStyle = '#2f2a26'; c.fillRect(x - 3, y - 3, 7, 7); c.fillStyle = '#ffffff'; c.fillRect(x - 2, y - 2, 5, 5);
        const [fx, fy] = [[0, 1], [1, 0], [0, -1], [-1, 0]][d.dir ?? 0]; c.fillStyle = '#db614f'; c.fillRect(x + fx * 2 - (fx ? 0 : 1), y + fy * 2 - (fy ? 0 : 1), fx ? 1 : 3, fy ? 1 : 3);
      } else {
        const col = d.kind === 'party' ? '#8fe08a' : d.kind === 'player' ? '#f3eee3' : d.kind === 'npc' ? '#f9c776' : d.kind === 'boss' ? '#ec5448' : '#f29b6b';
        const s = d.kind === 'party' ? 4 : 3;
        c.fillStyle = '#2f2a26'; c.fillRect(x - 1 - (s > 3 ? 1 : 0), y - 1 - (s > 3 ? 1 : 0), s, s); c.fillStyle = col; c.fillRect(x - (s > 3 ? 1 : 0), y - (s > 3 ? 1 : 0), s - 1, s - 1);
      }
    }
  }

  toggleWorld(force?: boolean): void {
    const open = force ?? this.world.hidden;
    if (open === !this.world.hidden) return;
    this.world.hidden = !open;
    if (open) this.renderWorld();
    this.onOpen(open);
  }

  private renderWorld(): void {
    const grid = $('worldGrid'); grid.replaceChildren();
    const here = this.zone.split('~')[0].split(':')[0] === 'home' ? 'cottages' : (zoneDef(this.zone.split('~')[0]) ? this.zone.split('~')[0] : zoneOfName(this.name) ?? 'town');
    const { party, friends } = this.people();
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg'); svg.setAttribute('class', 'wm-links');
    grid.append(svg);
    const cards = new Map<string, HTMLElement>();
    for (const z of ZONES) {
      const [c, r] = LAYOUT[z.id] ?? [0, 0];
      const card = document.createElement('div'); card.className = 'wm-zone' + (z.id === here ? ' here' : '') + (z.kind === 'field' ? ' field' : '');
      card.style.gridColumn = String(c + 1); card.style.gridRow = String(r + 1);
      const sc = this.scene(z.id);
      if (sc) { const img = document.createElement('canvas'); const t = sc.thumb(); img.width = t.width; img.height = t.height; img.getContext('2d')!.drawImage(t, 0, 0); card.append(img); }
      else { const ph = document.createElement('div'); ph.className = 'wm-unseen'; ph.textContent = '?'; card.append(ph); }
      const name = document.createElement('b'); name.textContent = z.name; card.append(name);
      const sub = document.createElement('small'); sub.textContent = BLURB[z.id] ?? ''; card.append(sub);
      const who = [...(party.get(z.id) ?? []).map(n => `🟢 ${n}`), ...(friends.get(z.id) ?? []).map(n => `★ ${n}`)];
      if (z.id === here) who.unshift('📍 You are here');
      if (who.length) { const w = document.createElement('div'); w.className = 'wm-who'; w.textContent = who.slice(0, 4).join('\n') + (who.length > 4 ? `\n+${who.length - 4} more` : ''); card.append(w); }
      card.title = z.kind === 'town' ? 'Town: a safe place, no fighting' : 'Open world';
      grid.append(card); cards.set(z.id, card);
    }
    const soon = document.createElement('div'); soon.className = 'wm-soon'; soon.textContent = 'The wide world · coming soon'; soon.style.gridColumn = '2'; soon.style.gridRow = '5'; grid.append(soon);
    // roads between the cards, drawn once they're laid out
    requestAnimationFrame(() => {
      const g = grid.getBoundingClientRect(), mid = (el: Element): [number, number] => { const r = el.getBoundingClientRect(); return [r.left - g.left + r.width / 2, r.top - g.top + r.height / 2]; };
      svg.setAttribute('viewBox', `0 0 ${g.width} ${g.height}`);
      const line = (a: [number, number], b: [number, number], cls = '') => { const l = document.createElementNS(svg.namespaceURI, 'line'); l.setAttribute('x1', String(a[0])); l.setAttribute('y1', String(a[1])); l.setAttribute('x2', String(b[0])); l.setAttribute('y2', String(b[1])); if (cls) l.setAttribute('class', cls); svg.append(l); };
      for (const [a, b] of LINKS) { const ea = cards.get(a), eb = cards.get(b); if (ea && eb) line(mid(ea), mid(eb)); }
      const m = cards.get('meadow'); if (m) { const [x, y] = mid(m); line([x, y], [x, mid(soon)[1] - 8], 'soon'); }
    });
  }
}
